import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import {
  Phone, Mail, MapPin, Package, CalendarDays, AlertTriangle,
  Plus, Pencil, Trash2, MoreVertical
} from 'lucide-react'
import { kunden as kundenApi } from '../../lib/api'
import { TopBar } from '../../components/layout/TopBar'
import { Card, CardHeader, CardTitle } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Badge, geraetStatusBadge, besuchStatusBadge, defektZustandBadge, defektStatusBadge } from '../../components/ui/Badge'
import { Modal } from '../../components/ui/Modal'
import { LoadingSpinner, ErrorMessage } from '../../components/ui/EmptyState'
import { formatDatum, BESUCH_TYP_LABEL } from '../../lib/utils'
import { KundeFormFields, useKundeForm } from './KundeFormPage'
import type { Kunde } from '../../types'

export function KundeDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [editOpen, setEditOpen] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)

  const { data, isLoading, error } = useQuery({
    queryKey: ['kunde', id],
    queryFn: () => kundenApi.get(id!),
    enabled: !!id,
  })

  const updateMutation = useMutation({
    mutationFn: (d: Partial<Kunde>) => kundenApi.update(id!, d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['kunde', id] }); setEditOpen(false) },
  })

  const deleteMutation = useMutation({
    mutationFn: () => kundenApi.delete(id!),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['kunden'] }); navigate('/kunden') },
  })

  if (isLoading) return <><TopBar title="Kunde" backTo="/kunden" /><LoadingSpinner /></>
  if (error || !data) return <><TopBar title="Fehler" backTo="/kunden" /><ErrorMessage message="Kunde nicht gefunden" /></>

  return (
    <>
      <TopBar
        title={data.firma ?? data.name}
        subtitle={data.firma ? data.name : undefined}
        backTo="/kunden"
        rightAction={
          <div className="relative">
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="p-2 rounded-xl hover:bg-gray-100"
            >
              <MoreVertical size={20} className="text-gray-600" />
            </button>
            {menuOpen && (
              <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-10 min-w-32 overflow-hidden">
                <button
                  onClick={() => { setMenuOpen(false); setEditOpen(true) }}
                  className="flex items-center gap-2 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 w-full"
                >
                  <Pencil size={14} /> Bearbeiten
                </button>
                <button
                  onClick={() => { setMenuOpen(false); setDeleteOpen(true) }}
                  className="flex items-center gap-2 px-4 py-3 text-sm text-red-600 hover:bg-red-50 w-full"
                >
                  <Trash2 size={14} /> Löschen
                </button>
              </div>
            )}
          </div>
        }
      />

      <div className="px-4 py-3 space-y-4 pb-8">
        {/* Kontakt */}
        <Card>
          <div className="space-y-3">
            {data.branche && <Badge variant="blue">{data.branche}</Badge>}
            {data.adresse && (
              <div className="flex items-start gap-3">
                <MapPin size={16} className="text-gray-400 mt-0.5 shrink-0" />
                <span className="text-sm text-gray-700">{data.adresse}, {data.plz} {data.ort}</span>
              </div>
            )}
            {data.telefon && (
              <a href={`tel:${data.telefon}`} className="flex items-center gap-3 group">
                <Phone size={16} className="text-gray-400 shrink-0" />
                <span className="text-sm text-blue-600 group-hover:underline">{data.telefon}</span>
              </a>
            )}
            {data.email && (
              <a href={`mailto:${data.email}`} className="flex items-center gap-3 group">
                <Mail size={16} className="text-gray-400 shrink-0" />
                <span className="text-sm text-blue-600 group-hover:underline truncate">{data.email}</span>
              </a>
            )}
            {data.notizen && (
              <p className="text-sm text-gray-600 bg-gray-50 rounded-xl p-3">{data.notizen}</p>
            )}
          </div>
        </Card>

        {/* Quick Actions */}
        <div className="grid grid-cols-3 gap-2">
          <Button variant="secondary" size="sm" icon={<CalendarDays size={14} />} onClick={() => navigate(`/besuche/neu?kunde_id=${id}`)}>Besuch</Button>
          <Button variant="secondary" size="sm" icon={<Package size={14} />} onClick={() => navigate(`/inventar/neu?kunde_id=${id}`)}>Gerät</Button>
          <Button variant="secondary" size="sm" icon={<AlertTriangle size={14} />} onClick={() => navigate(`/defekte/neu?kunde_id=${id}`)}>Defekt</Button>
        </div>

        {/* Geräte */}
        <div>
          <CardHeader>
            <CardTitle><Package size={16} className="inline mr-2" />Geräte ({data.geraete.length})</CardTitle>
            <Button size="sm" variant="ghost" icon={<Plus size={14} />} onClick={() => navigate(`/inventar/neu?kunde_id=${id}`)}>Hinzufügen</Button>
          </CardHeader>
          {data.geraete.length > 0 ? (
            <div className="space-y-2">
              {data.geraete.map((g) => (
                <Card key={g.id} clickable onClick={() => navigate(`/inventar/${g.id}`)} padding className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center shrink-0">
                    <Package size={18} className="text-gray-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-gray-900">{g.modell}</p>
                    <p className="text-xs text-gray-500">{g.seriennummer ?? 'Keine SN'} · {g.standort}</p>
                  </div>
                  {geraetStatusBadge(g.status)}
                </Card>
              ))}
            </div>
          ) : (
            <Card><p className="text-sm text-gray-500 text-center py-3">Keine Geräte</p></Card>
          )}
        </div>

        {/* Besuche */}
        <div>
          <CardHeader>
            <CardTitle><CalendarDays size={16} className="inline mr-2" />Besuche ({data.besuche.length})</CardTitle>
            <Button size="sm" variant="ghost" icon={<Plus size={14} />} onClick={() => navigate(`/besuche/neu?kunde_id=${id}`)}>Planen</Button>
          </CardHeader>
          {data.besuche.length > 0 ? (
            <div className="space-y-2">
              {data.besuche.slice(0, 5).map((b) => (
                <Card key={b.id} clickable onClick={() => navigate(`/besuche/${b.id}`)} className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-50 rounded-xl flex flex-col items-center justify-center shrink-0">
                    <span className="text-blue-700 font-bold text-sm">{formatDatum(b.datum, 'dd')}</span>
                    <span className="text-blue-500 text-xs">{formatDatum(b.datum, 'MMM')}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{BESUCH_TYP_LABEL[b.typ] ?? b.typ}</p>
                    <p className="text-xs text-gray-500 truncate">{b.techniker}</p>
                  </div>
                  {besuchStatusBadge(b.status)}
                </Card>
              ))}
            </div>
          ) : (
            <Card><p className="text-sm text-gray-500 text-center py-3">Keine Besuche</p></Card>
          )}
        </div>

        {/* Defekte */}
        {data.defekte.length > 0 && (
          <div>
            <CardHeader>
              <CardTitle><AlertTriangle size={16} className="inline mr-2" />Defekte ({data.defekte.length})</CardTitle>
            </CardHeader>
            <div className="space-y-2">
              {data.defekte.map((d) => (
                <Card key={d.id} clickable onClick={() => navigate(`/defekte/${d.id}`)} className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{d.beschreibung}</p>
                    <p className="text-xs text-gray-500">{formatDatum(d.erstellt_am)}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {defektZustandBadge(d.zustand)}
                    {defektStatusBadge(d.status)}
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      <Modal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title="Kunde bearbeiten"
        footer={
          <div className="flex gap-2">
            <Button variant="outline" fullWidth onClick={() => setEditOpen(false)}>Abbrechen</Button>
            <Button fullWidth loading={updateMutation.isPending} onClick={() => document.getElementById('kunde-edit-submit')?.click()}>Speichern</Button>
          </div>
        }
      >
        <form
          onSubmit={(e) => {
            e.preventDefault()
            const fd = new FormData(e.currentTarget)
            const d = Object.fromEntries(fd.entries())
            updateMutation.mutate(d as Partial<Kunde>)
          }}
          className="space-y-4"
        >
          <EditKundeForm data={data} />
          <button id="kunde-edit-submit" type="submit" className="hidden" />
        </form>
      </Modal>

      {/* Löschen bestätigen */}
      <Modal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        title="Kunde löschen?"
        footer={
          <div className="flex gap-2">
            <Button variant="outline" fullWidth onClick={() => setDeleteOpen(false)}>Abbrechen</Button>
            <Button
              variant="danger"
              fullWidth
              loading={deleteMutation.isPending}
              onClick={() => deleteMutation.mutate()}
            >
              Löschen
            </Button>
          </div>
        }
      >
        <p className="text-sm text-gray-700">
          Soll <strong>{data.firma ?? data.name}</strong> wirklich gelöscht werden?
          Alle verknüpften Geräte, Besuche und Defekte werden ebenfalls entfernt.
        </p>
        {deleteMutation.error && (
          <p className="text-sm text-red-600 mt-3">{(deleteMutation.error as Error).message}</p>
        )}
      </Modal>
    </>
  )
}

function EditKundeForm({ data }: { data: Kunde }) {
  const form = useKundeForm(data)
  return <KundeFormFields form={form} />
}
